import { ListPlus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/stores/session';
import {
  useCreateCustomFieldDef,
  useCustomFieldDefs,
  useDeleteCustomFieldDef,
} from '../queries';
import type { CustomFieldDef, FieldType } from '../types';

/** Entity options the admin can attach fields to. Mirrors what the FE actually
 *  renders panels for (Wave 14/15). Extending this list does NOT require a BE
 *  change — `entity` is a free string on `CustomFieldDef`. */
const ENTITIES: { value: string; label: string }[] = [
  { value: 'crm.deal', label: 'Deal' },
  { value: 'crm.company', label: 'Company' },
  { value: 'crm.lead', label: 'Lead' },
  { value: 'crm.contact', label: 'Contact' },
  { value: 'projects.project', label: 'Project' },
  { value: 'hrm.employee', label: 'Employee' },
  { value: 'finance.invoice', label: 'Invoice' },
];

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select (options)' },
  { value: 'checkbox', label: 'Checkbox' },
];

const ALL = 'all';

export function CustomFieldDefsPage() {
  const role = useSession((s) => s.role);
  const isAdmin = role === 'founder' || role === 'super_admin';
  const [entityFilter, setEntityFilter] = useState<string>(ALL);

  // We fetch defs per entity; when "all" is selected, request each known entity
  // — keeps the BE filter behavior intact (it always expects `?entity=`).
  const requestedEntities = entityFilter === ALL
    ? ENTITIES.map((e) => e.value)
    : [entityFilter];

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Custom fields</h1>
        <p className="text-muted-foreground text-sm">
          Org-defined attributes that appear on entity detail pages. Adding a
          field shows it on every record of the selected entity.
        </p>
      </div>

      {isAdmin ? <NewDefCard /> : null}

      <div className="flex items-center gap-3">
        <Label htmlFor="filter">Show</Label>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger id="filter" className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All entities</SelectItem>
            {ENTITIES.map((e) => (
              <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-4">
        {requestedEntities.map((entity) => (
          <EntityDefsCard key={entity} entity={entity} canManage={isAdmin} />
        ))}
      </div>
    </div>
  );
}

interface EntityDefsCardProps {
  entity: string;
  canManage: boolean;
}

function EntityDefsCard({ entity, canManage }: EntityDefsCardProps) {
  const defs = useCustomFieldDefs(entity);
  const remove = useDeleteCustomFieldDef(entity);
  const label = ENTITIES.find((e) => e.value === entity)?.label ?? entity;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{label}</CardTitle>
        <CardDescription>
          <code className="text-xs">{entity}</code>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {defs.isPending ? (
          <Skeleton className="h-12 w-full" />
        ) : !defs.data || defs.data.length === 0 ? (
          <p className="text-muted-foreground text-sm">No fields yet.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {defs.data.map((def) => (
              <DefRow
                key={def.id}
                def={def}
                canManage={canManage}
                onDelete={() => remove.mutate(def.id)}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function DefRow({
  def, canManage, onDelete,
}: { def: CustomFieldDef; canManage: boolean; onDelete: () => void }) {
  return (
    <li className="flex items-center justify-between gap-3 p-3 text-sm">
      <div className="min-w-0">
        <p className="font-medium">{def.label}</p>
        <p className="text-muted-foreground truncate text-xs">
          <code>{def.key}</code> · {def.field_type}
          {def.required ? ' · required' : ''}
          {def.field_type === 'select' && def.options.length > 0
            ? ` · ${def.options.join(', ')}`
            : ''}
        </p>
      </div>
      {canManage ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          aria-label={`Delete ${def.label}`}
        >
          <Trash2 className="size-4" />
        </Button>
      ) : (
        <Badge variant="outline">Read only</Badge>
      )}
    </li>
  );
}

function NewDefCard() {
  const [entity, setEntity] = useState<string>('crm.deal');
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState<FieldType>('text');
  const [optionsText, setOptionsText] = useState('');
  const [required, setRequired] = useState(false);

  const create = useCreateCustomFieldDef(entity);

  // For SELECT, the BE expects a list of strings. We parse on submit so the
  // user sees their raw input until they hit Add.
  const options = useMemo(
    () => optionsText.split(',').map((s) => s.trim()).filter(Boolean),
    [optionsText],
  );

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!key.trim() || !label.trim()) return;
    await create.mutateAsync({
      entity,
      key: key.trim(),
      label: label.trim(),
      field_type: fieldType,
      options: fieldType === 'select' ? options : [],
      required,
    });
    setKey('');
    setLabel('');
    setOptionsText('');
    setRequired(false);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ListPlus className="size-4" /> Add a custom field
        </CardTitle>
        <CardDescription>
          Adds an editable attribute to every record of the chosen entity.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-3 sm:grid-cols-2" onSubmit={onSubmit}>
          <div className="space-y-1">
            <Label htmlFor="def-entity">Entity</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger id="def-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENTITIES.map((e) => (
                  <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="def-type">Type</Label>
            <Select value={fieldType} onValueChange={(v) => setFieldType(v as FieldType)}>
              <SelectTrigger id="def-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="def-label">Label</Label>
            <Input
              id="def-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Industry"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="def-key">Key</Label>
            <Input
              id="def-key"
              value={key}
              onChange={(e) => setKey(e.target.value.replace(/\s+/g, '_').toLowerCase())}
              placeholder="industry"
            />
          </div>
          {fieldType === 'select' ? (
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="def-options">Options (comma separated)</Label>
              <Input
                id="def-options"
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                placeholder="SaaS, Agency, Marketplace"
              />
            </div>
          ) : null}
          <div className="flex items-center gap-2 sm:col-span-2">
            <Switch
              id="def-required"
              checked={required}
              onCheckedChange={setRequired}
              aria-label="Required"
            />
            <Label htmlFor="def-required">Required</Label>
          </div>
          <div className="sm:col-span-2">
            <Button
              type="submit"
              disabled={create.isPending || !key.trim() || !label.trim()}
            >
              {create.isPending ? 'Adding…' : 'Add field'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
